import { useState } from 'react';
import { customPayRunGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { CPR_PERMISSIONS, NO_PERMISSION_NOTE } from '../permissions';
import { usePermissionCheck } from '../../identity/state/usePermissions';

const REVOKE_AFTER_MS = 10_000;

/** Saves a fetched file through an anchor click. The bytes came from an authenticated fetch (cookie auth), so no public URL is ever exposed. */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
}

/** Downloads the run's payroll records as CSV (no SSN, no bank data). Needs the read permission; the server checks it and is audited. */
export function ExportCsvButton({ runId }: { runId: string }) {
  const can = usePermissionCheck();
  const allowed = can(CPR_PERMISSIONS.read);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleExport() {
    setBusy(true);
    setError(null);
    try {
      const file = await customPayRunGateway.exportCsv(runId);
      saveBlob(file.blob, file.fileName);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not export this run.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="cpr-btn cpr-btn--outline"
        disabled={busy || !allowed}
        title={allowed ? 'Download the payroll records of this run as a CSV file' : NO_PERMISSION_NOTE}
        onClick={() => void handleExport()}
      >
        {busy ? 'Exporting…' : 'Export'}
      </button>
      {error && (
        <span className="error" role="alert">
          {error}
        </span>
      )}
    </>
  );
}
